import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';

import { Client } from '../entity/client';
import { Type } from '../entity/type';
import { withFirstTeamIds } from '../misc/rxjs-operators';
import { TeamsSelectors } from '../state/teams/teams.selectors';
import { ToastActions } from '../state/toast/toast.actions';
import {
  toClientListTypeTransformer,
  toClientTypeTransformer,
  toClientTypeTypesTransformer,
} from '../state/transformers/transformers';
import { ClientType, ClientTypeType } from '../state/types';
import { FirmCollaboratorMetaType, FirmCollaboratorType } from '../state/types/firm-collaborator.types';
import { BLService } from './bl.service';

@Injectable({
  providedIn: 'root',
})
export class ClientService extends BLService {
  private RESOURCE_ENDPOINT = 'client';

  clientsBS = new BehaviorSubject({});
  clients$: Observable<object>;

  constructor() {
    super();
    this.useCache(['TYPES']);
    this.clients$ = this.clientsBS.asObservable();
  }

  getAllFilteredClients(): Observable<Client[]> {
    return withFirstTeamIds(this.store).pipe(
      switchMap(({ ids }) =>
        this.httpGet<Client[]>(this.RESOURCE_ENDPOINT, this.httpAuthHeader.getHeaderWithoutBody(ids)),
      ),
    );
  }

  getAllClients(): Observable<Client[]> {
    return withFirstTeamIds(this.store).pipe(
      switchMap(() =>
        this.store.select(TeamsSelectors.selectTeamSelector).pipe(
          first(),
          switchMap(({ ids }) =>
            this.httpGet<Client[]>(this.RESOURCE_ENDPOINT, this.httpAuthHeader.getHeaderWithoutBody(ids)),
          ),
        ),
      ),
    );
  }

  getAllClientTypes(): Observable<ClientType[]> {
    return this.getAllClients().pipe(map((clients) => clients.map(toClientTypeTransformer.transform)));
  }

  getAllFilteredClientTypes(): Observable<ClientType[]> {
    return this.getAllFilteredClients().pipe(map((clients) => clients.map(toClientListTypeTransformer.transform)));
  }

  getAllArchivedClients(): Observable<Client[]> {
    return this.httpGet<Client[]>(`${this.RESOURCE_ENDPOINT}/archived`);
  }

  getClientById(id: number): Observable<Client> {
    return this.httpGet<Client>(`${this.RESOURCE_ENDPOINT}/${id}`);
  }

  // getClientsWithAdvisoryTool(): Observable<Client[]> {
  //   return this.getAllFilteredClients().pipe(
  //     map((clients) => clients.filter((client) => client.advisoryTool && !!client.cloudApiKey)),
  //   );
  // }

  recreateHash(id: number): Observable<Client> {
    return this.httpPost<Client>(`${this.RESOURCE_ENDPOINT}/${id}/recreate-hash`, null).pipe(
      tap(() =>
        this.store.dispatch(
          ToastActions.showInfoMessage({ summary: 'Programnyckel', detail: 'Ny programnyckel har skapats' }),
        ),
      ),
    );
  }

  getClientsTypes(): Observable<Type[]> {
    if (!this.hasCacheExpired('TYPES')) {
      return this.getCacheObservable<Type[]>('TYPES');
    }

    return this.httpGet<Type[]>(`${this.RESOURCE_ENDPOINT}/types`).pipe(
      tap((result) => this.updateCache(result, 'TYPES')),
    );
  }

  getClientTypes(): Observable<ClientTypeType[]> {
    return this.getClientsTypes().pipe(map((types) => types.map(toClientTypeTypesTransformer.transform)));
  }

  addClient(client: Client, selectedTeams: number[] = []): Observable<Client> {
    const options = this.httpAuthHeader.getHeaderWithoutContentType(selectedTeams);
    return this.httpPut<Client>(this.RESOURCE_ENDPOINT, client, options);
  }

  upsertClientType(client: ClientType): Observable<ClientType> {
    return withFirstTeamIds(this.store).pipe(switchMap(({ ids }) => this.upsertClientTypeWithTeams(client, ids)));
  }

  upsertClientTypeWithTeams(client: ClientType, teamIds: number[]): Observable<ClientType> {
    const options = this.httpAuthHeader.getHeaderWithoutContentType(teamIds);
    return this.httpPut<ClientType>(this.RESOURCE_ENDPOINT, client, options);
  }

  deleteClient(id: number): Observable<Client> {
    return this.httpDelete<Client>(`${this.RESOURCE_ENDPOINT}/${id}`);
  }

  verifyCloudApiKey(cloudApiKey: string) {
    return this.httpGet<{ name: string; orgNumber: string; cloudApiKey: string }>(
      `${this.RESOURCE_ENDPOINT}/verify-key/${cloudApiKey}`,
    );
  }

  getAgentUrl(clientId: number) {
    return this.httpGet<any>(`${this.RESOURCE_ENDPOINT}/agent-call/${clientId}`);
  }

  getIntegrationKey(clientId: number) {
    return this.httpGet<any>(`${this.RESOURCE_ENDPOINT}/blapp-integration-key/${clientId}`);
  }

  activateSustainabilityReporting(clientId: number): Observable<void> {
    return this.httpPost<void>(`${this.RESOURCE_ENDPOINT}/${clientId}/sustainability-reporting`, null);
  }

  deactivateSustainabilityReporting(clientId: number): Observable<void> {
    return this.httpDelete<void>(`${this.RESOURCE_ENDPOINT}/${clientId}/sustainability-reporting`);
  }

  archiveDeletableClient(id: number): Observable<void> {
    return this.httpPost<void>(`${this.RESOURCE_ENDPOINT}/${id}/archive`, null);
  }

  getFirmCollaborators(clientId: number): Observable<FirmCollaboratorMetaType> {
    return this.httpGet<FirmCollaboratorMetaType>(`firm-collaborators/${clientId}`);
  }

  updateFirmCollaborators(clientId: number, userId: number, firmKeys: string[]): Observable<FirmCollaboratorType[]> {
    return this.httpPost<FirmCollaboratorType[]>(`firm-collaborators/${clientId}`, {
      userId,
      collaborators: firmKeys,
    });
  }
}
